import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, Button, Box, IconButton, Drawer, List, ListItem, ListItemText } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink } from 'react-router-dom';

function Header({ user, onLogout }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  return (
    <AppBar position="static">
      <Toolbar sx={{ minHeight: { xs: '64px', sm: '70px' } }}>
        <Box
          component={RouterLink}
          to="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            color: 'inherit',
            mr: 2,
            '&:hover': {
              opacity: 0.8
            }
          }}
        >
          <img 
            src="/logo.png" 
            alt="ComplainAI Logo" 
            style={{ 
              height: '50px',
              width: 'auto',
              marginRight: '12px'
            }} 
          />
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600,
                fontSize: '0.85rem',
                opacity: 0.9,
                letterSpacing: '0.5px'
              }}
            >
              Your Voice. Your Money Back.
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ flexGrow: 1 }} />
        
        {/* Desktop Navigation */}
        {user ? (
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
            <Button color="inherit" component={RouterLink} to="/dashboard">
              My Letters
            </Button>
            <Button color="inherit" component={RouterLink} to="/dispute/new">
              Create Letter
            </Button>
            <Button color="inherit" component={RouterLink} to="/profile">
              Profile
            </Button>
            <Button color="inherit" onClick={onLogout}>
              Logout
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
            <Button 
              color="inherit" 
              onClick={() => {
                if (window.location.pathname === '/') {
                  document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
                } else {
                  window.location.href = '/#how-it-works';
                }
              }}
            >
              How It Works
            </Button>
            <Button 
              color="inherit" 
              onClick={() => {
                if (window.location.pathname === '/') {
                  document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                } else {
                  window.location.href = '/#pricing';
                }
              }}
            >
              Pricing
            </Button>
            <Button color="inherit" component={RouterLink} to="/login">
              Login
            </Button>
            <Button 
              color="inherit" 
              component={RouterLink} 
              to="/register"
              variant="outlined"
              sx={{ borderColor: 'white', '&:hover': { borderColor: 'white' } }}
            >
              Sign Up Free
            </Button>
          </Box>
        )}

        {/* Mobile Menu Button */}
        <IconButton
          color="inherit"
          edge="end"
          onClick={toggleMobileMenu}
          sx={{ display: { xs: 'block', md: 'none' } }}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>

      {/* Mobile Drawer Menu */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
        sx={{ display: { xs: 'block', md: 'none' } }}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: '#ffffff',
            color: '#000000'
          }
        }}
      >
        <Box sx={{ height: '100%' }}>
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderBottom: '1px solid #e0e0e0'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Menu
            </Typography>
            <IconButton onClick={toggleMobileMenu} sx={{ color: '#000000' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <List sx={{ pt: 2 }}>
            {user ? (
              <>
                <ListItem 
                  component={RouterLink} 
                  to="/dashboard" 
                  onClick={toggleMobileMenu}
                  sx={{ 
                    py: 1.5,
                    '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' }
                  }}
                >
                  <ListItemText 
                    primary="My Letters" 
                    primaryTypographyProps={{ 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}
                  />
                </ListItem>
                <ListItem 
                  component={RouterLink} 
                  to="/dispute/new" 
                  onClick={toggleMobileMenu}
                  sx={{ 
                    py: 1.5,
                    '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' }
                  }}
                >
                  <ListItemText 
                    primary="Create Letter" 
                    primaryTypographyProps={{ 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}
                  />
                </ListItem>
                <ListItem 
                  component={RouterLink} 
                  to="/profile" 
                  onClick={toggleMobileMenu}
                  sx={{ 
                    py: 1.5,
                    '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' }
                  }}
                >
                  <ListItemText 
                    primary="Profile" 
                    primaryTypographyProps={{ 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}
                  />
                </ListItem>
                <ListItem 
                  onClick={() => { onLogout(); toggleMobileMenu(); }}
                  sx={{ 
                    py: 1.5,
                    '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' }
                  }}
                >
                  <ListItemText 
                    primary="Logout" 
                    primaryTypographyProps={{ 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}
                  />
                </ListItem>
              </>
            ) : (
              <>
                <ListItem 
                  onClick={() => {
                    toggleMobileMenu();
                    if (window.location.pathname === '/') {
                      setTimeout(() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' }), 100);
                    } else {
                      window.location.href = '/#how-it-works';
                    }
                  }}
                  sx={{ 
                    py: 1.5,
                    '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' }
                  }}
                >
                  <ListItemText 
                    primary="How It Works" 
                    primaryTypographyProps={{ 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}
                  />
                </ListItem>
                <ListItem 
                  onClick={() => {
                    toggleMobileMenu();
                    if (window.location.pathname === '/') {
                      setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 100);
                    } else {
                      window.location.href = '/#pricing';
                    }
                  }}
                  sx={{ 
                    py: 1.5,
                    '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' }
                  }}
                >
                  <ListItemText 
                    primary="Pricing" 
                    primaryTypographyProps={{ 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}
                  />
                </ListItem>
                <ListItem 
                  component={RouterLink} 
                  to="/login" 
                  onClick={toggleMobileMenu}
                  sx={{ 
                    py: 1.5,
                    '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' }
                  }}
                >
                  <ListItemText 
                    primary="Login" 
                    primaryTypographyProps={{ 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}
                  />
                </ListItem>
                <ListItem 
                  component={RouterLink} 
                  to="/register" 
                  onClick={toggleMobileMenu}
                  sx={{ 
                    py: 1.5,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    mx: 2,
                    mt: 1,
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                >
                  <ListItemText 
                    primary="Sign Up Free" 
                    primaryTypographyProps={{ 
                      fontWeight: 600,
                      fontSize: '1rem',
                      textAlign: 'center'
                    }}
                  />
                </ListItem>
              </>
            )}
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
}

export default Header;
